
import {
  computed,
  defineComponent,
  nextTick,
  onBeforeMount,
  onMounted,
  onBeforeUnmount,
  PropType,
  Ref,
  ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Editor from 'primevue/editor';
import {
  ColorFormatEnum,
  FieldConfig,
  ParserFieldObject,
  Property,
} from '@/types';
import EmojisButton from '@/components/fields/partials/EmojisButton.vue';
import VariablesButton from '@/components/fields/partials/VariablesButton.vue';
import Quill, { applyLinkToRange, FontStyle } from '@/components/template-builder/utils/quill-setup';
import CustomColorPicker from '@/components/fields/partials/CustomColorPicker.vue';
import OverlayPanel from 'primevue/overlaypanel';
import Tooltip from 'primevue/tooltip';
import { getLineHeightOptions } from '@/configs/line-height';
import BaseInputNumber from '@/components/fields/partials/BaseInputNumber.vue';
import AIButton from '@/components/fields/AIButton.vue';
import GoogleFontPickerModal from '@/components/modals/GoogleFontPickerModal.vue';
import WysiwygLinkDialog from '@/components/template-builder/fields/WysiwygLinkDialog.vue';
import { useEditorBackground } from '@/composables/useEditorBackground';
import { getTemplateIframeDocument, isDisplayTemplate, isEmailTemplate } from '@/components/template-builder/utils/helpers';
import { TemplateEditorState, refreshNextSteps, updateTemplateConfigurationNextIdTemplate } from '@/composables/template-editor/TemplateEditor';
import { useGoogleFonts } from '@/composables/google-fonts/useGoogleFonts';
import { getVariablesWithCollectionContext, getMetadataVariables } from '@/composables/template-builder/collectionContext';
import { getCustomContactProperties } from '@/configs/custom-contact-properties';

export default defineComponent({
  name: 'WysiwygEditor',
  components: {
    AIButton,
    BaseInputNumber,
    OverlayPanel,
    CustomColorPicker,
    VariablesButton,
    EmojisButton,
    Editor,
    GoogleFontPickerModal,
    WysiwygLinkDialog,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    configs: {
      type: Object as PropType<FieldConfig>,
      required: true,
    },

    parserValues: {
      type: Object as PropType<ParserFieldObject>,
      required: true,
    },
  },

  emits: {
    'on-change-properties': Object,
  },

  setup(props, context) {
    const { t } = useI18n();
    const properties: Ref<Array<Property>> = ref(props.parserValues.properties);
    const inputValue: Ref<string> = ref(properties.value[0].value ?? props.configs.options.defaultValue);
    const editorRef = ref();
    const editorKey = ref(0);
    const overlayPanelRef = ref();
    const overlayPanelBackgroundRef = ref();
    const overlayPanelLetterSpacingRef = ref();
    const colorFormat: Ref<string> = ref(ColorFormatEnum.HEX);
    const lineHeightOptions = getLineHeightOptions(t);
    lineHeightOptions.unshift({
      label: t('templateBuilder.fields.defaultLineHeight'),
      value: '',
    });
    const lineHeightList = ref(lineHeightOptions);
    const letterSpacingValue = ref();
    const selectedRange = ref();
    const { registerEditor, unregisterEditor } = useEditorBackground();
    const enrichedVariables = ref(props.configs.options.variablesList || []);

    // Google Fonts integration
    const isGoogleFontsModalVisible = ref(false);
    const {
      injectFontToIframe,
      getDetectedTemplateFonts,
      isEmailSafe,
    } = useGoogleFonts();

    const showGoogleFontsButton = computed(() => {
      const templateType = TemplateEditorState?.template?.type;
      return isDisplayTemplate(templateType) || isEmailTemplate(templateType);
    });

    const isEmailTemplateType = computed(() => {
      const templateType = TemplateEditorState?.template?.type;
      return isEmailTemplate(templateType);
    });

    const iframeDocumentForModal = computed(() => {
      try {
        return getTemplateIframeDocument() || null;
      } catch {
        return null;
      }
    });

    const currentGoogleFontInQuill = computed(() => {
      if (!editorRef.value?.quill) return '';
      const format = editorRef.value.quill.getFormat();
      const fontValue = format?.font || '';
      if (!fontValue) return '';
      const match = fontValue.match(/"([^"]+)"/);
      return match ? match[1] : '';
    });

    const openGoogleFontsModal = () => {
      isGoogleFontsModalVisible.value = true;
    };

    // Saved Quill selection for Google Fonts picker (selection is lost when clicking dropdown items)
    const savedFontPickerRange = ref<{ index: number; length: number } | null>(null);

    // Override canAdd to bypass Parchment's Registry.query node check.
    // Parchment's default canAdd first checks Registry.query(node, scope) which can
    // return null in certain contexts (e.g. when formatText is called programmatically),
    // causing the format to silently fail even when the value is in the whitelist.
    // We keep the whitelist validation but skip the node scope check.
    const origCanAdd = FontStyle.canAdd;
    FontStyle.canAdd = function patchedCanAdd(node: HTMLElement, value: any): boolean {
      // Try the original check first (works for standard Quill dropdown fonts)
      const origResult = origCanAdd.call(FontStyle, node, value);
      if (origResult) return true;
      // Fallback: if the node check failed but the value is in the whitelist, allow it
      if (FontStyle.whitelist != null && typeof value === 'string') {
        return FontStyle.whitelist.indexOf(value) > -1;
      }
      return false;
    };

    // Normalize a font-family CSS value through browser round-trip.
    // Browsers normalize quotes, spacing, casing when setting style.fontFamily.
    // Quill's StyleAttributor reads back from DOM and compares with whitelist,
    // so the whitelist value MUST match the browser-normalized form.
    const normalizeFontValue = (fontValue: string): string => {
      const tempSpan = document.createElement('span');
      tempSpan.style.fontFamily = fontValue;
      return tempSpan.style.fontFamily || fontValue;
    };

    // Apply a Google Font format to the saved selection
    const applyGoogleFont = (fontValue: string): void => {
      const quill = editorRef.value?.quill;
      if (!quill) return;

      const range = savedFontPickerRange.value || quill.getSelection();
      if (!range) return;

      // Use browser-normalized value so it matches what Quill reads back from DOM
      const normalized = normalizeFontValue(fontValue);

      // Ensure the normalized value is in the whitelist
      if (FontStyle.whitelist && !FontStyle.whitelist.includes(normalized)) {
        FontStyle.whitelist.push(normalized);
      }

      quill.focus();
      quill.setSelection(range.index, range.length, 'silent');

      if (range.length > 0) {
        quill.formatText(range.index, range.length, 'font', normalized, 'user');
      } else {
        quill.format('font', normalized);
      }

      handleValueChange();
    };

    // Add a Google Font to Quill's font picker DOM and whitelist
    const addFontToQuillPicker = (fontFamily: string, fontValue: string): void => {
      if (!editorRef.value?.quill) return;

      // Normalize through browser round-trip so value matches what Quill reads back from DOM
      const normalized = normalizeFontValue(fontValue);

      // Add to FontStyle whitelist if not already present
      if (FontStyle.whitelist && !FontStyle.whitelist.includes(normalized)) {
        FontStyle.whitelist.push(normalized);
      }

      // Find the Quill font picker in the toolbar DOM
      const editorEl = editorRef.value.$el;
      if (!editorEl) return;

      const fontPicker = editorEl.querySelector('.ql-font.ql-picker');
      if (!fontPicker) return;

      // Check if already added to picker DOM
      const pickerOptions = fontPicker.querySelector('.ql-picker-options');
      if (!pickerOptions) return;

      const escapedValue = normalized.replace(/"/g, '\\"');
      const existingItem = pickerOptions.querySelector(`.ql-picker-item[data-value="${escapedValue}"]`);
      if (existingItem) return;

      // Create new picker item in the custom dropdown
      const newItem = document.createElement('span');
      newItem.classList.add('ql-picker-item');
      newItem.setAttribute('data-value', normalized);
      newItem.setAttribute('data-label', fontFamily);
      newItem.setAttribute('tabindex', '0');
      newItem.setAttribute('role', 'button');

      // Save selection on mousedown (before focus is lost)
      newItem.addEventListener('mousedown', () => {
        const quill = editorRef.value?.quill;
        if (quill) {
          const range = quill.getSelection();
          if (range) {
            savedFontPickerRange.value = { index: range.index, length: range.length };
          }
        }
      });

      // Apply font on click with saved selection
      newItem.addEventListener('click', () => {
        // Update picker UI: remove ql-selected from others, mark this one
        pickerOptions.querySelectorAll('.ql-picker-item').forEach((el: Element) => el.classList.remove('ql-selected'));
        newItem.classList.add('ql-selected');

        // Update picker label
        const pickerLabel = fontPicker.querySelector('.ql-picker-label');
        if (pickerLabel) {
          pickerLabel.setAttribute('data-value', normalized);
          pickerLabel.setAttribute('data-label', fontFamily);
        }

        // Close the picker dropdown
        fontPicker.classList.remove('ql-expanded');

        // Apply font format with saved selection
        applyGoogleFont(normalized);
      });

      pickerOptions.appendChild(newItem);
    };

    // Handle Google Font selection from the modal
    const handleGoogleFontSelect = (fontData: { family: string; value: string; isEmailSafe: boolean }) => {
      if (!editorRef.value?.quill) return;

      const { family, value: fontValue } = fontData;

      // Add to Quill picker DOM and whitelist
      addFontToQuillPicker(family, fontValue);

      // Inject font CSS into the template iframe
      const iframeDoc = getTemplateIframeDocument();
      if (iframeDoc && !fontData.isEmailSafe) {
        injectFontToIframe(family, iframeDoc);
      }

      // Also inject into parent document for Quill editor preview
      if (!fontData.isEmailSafe) {
        injectFontToIframe(family, document);
      }

      // Apply the font with saved selection
      applyGoogleFont(fontValue);
    };

    const cleanTrailingEmptyBlock = (html: string): string => html.replace(/<p><br><\/p>\s*$/, '');

    let pendingValueChange: number | null = null;

    const applyValueChange = () => {
      pendingValueChange = null;
      const quill = editorRef.value?.quill;
      if (!quill) return;
      // Repair anchor hrefs that the browser may have normalized (e.g. drag & drop
      // turns href="{var=...}" into an absolute URL). data-spm-href holds the raw value.
      quill.root.querySelectorAll('a[data-spm-href]').forEach((a: HTMLAnchorElement) => {
        const clean = a.getAttribute('data-spm-href');
        if (clean && a.getAttribute('href') !== clean) {
          a.setAttribute('href', clean);
        }
      });
      const content = cleanTrailingEmptyBlock(quill.root.innerHTML);
      const updatedProperties: Array<Property> = properties.value.map((prop: Property) => {
        const updatedProperty = prop;
        updatedProperty.value = content;
        return updatedProperty;
      });
      context.emit('on-change-properties', {
        selector: props.parserValues.selector,
        properties: updatedProperties,
      });
    };

    // Debounced: the per-keystroke template update pipeline is heavy. Never write
    // inputValue back here — changing the Editor's modelValue prop makes PrimeVue
    // rebuild the whole Quill document while unfocused, corrupting typing (SC-3791)
    const handleValueChange = () => {
      if (pendingValueChange !== null) window.clearTimeout(pendingValueChange);
      pendingValueChange = window.setTimeout(applyValueChange, 150);
    };

    const flushValueChange = () => {
      if (pendingValueChange === null) return;
      window.clearTimeout(pendingValueChange);
      applyValueChange();
    };

    // Blur flushes pending edits so save / widget switch read up-to-date state
    const handleSelectionChange = (event: { range: unknown }) => {
      if (!event.range) flushValueChange();
    };

    const insertContent = (value: string | number | undefined) => {
      editorRef.value.quill.insertText(editorRef.value.quill.selection.savedRange.index, value);
      handleValueChange();
    };

    const replaceContent = (value: string) => {
      editorRef.value.quill.clipboard.dangerouslyPasteHTML(value);
      handleValueChange();
    };

    const toggleColorPicker = (event: MouseEvent) => {
      overlayPanelRef.value.toggle(event);
    };

    const toggleColorPickerBackground = (event: MouseEvent) => {
      overlayPanelBackgroundRef.value.toggle(event);
    };

    const changeColor = (color: string) => {
      editorRef.value.quill.format('color', color);
      handleValueChange();
    };

    const changeColorBackground = (color: string) => {
      editorRef.value.quill.format('background-color', color);
      handleValueChange();
    };
    const saveRangeSelection = (event: MouseEvent) => {
      // Store selected range (because when opening the panel, the range disappears)
      const range = editorRef.value.quill.getSelection();
      if (range !== null) {
        selectedRange.value = range;
      }
    };
    const toggleLetterSpacing = (event: MouseEvent) => {
      letterSpacingValue.value = null;
      overlayPanelLetterSpacingRef.value.toggle(event);
      editorRef.value.quill.setSelection(selectedRange.value);
    };

    const changeLetterSpacing = (value: any) => {
      if (selectedRange.value) {
        editorRef.value.quill.setSelection(selectedRange.value);
        editorRef.value.quill.formatText(selectedRange.value.index, selectedRange.value.length, {
          'letter-spacing': `${value}px`,
        });
      }

      handleValueChange();
    };

    // ---- Custom Link Dialog ----

    const isLinkDialogVisible = ref(false);
    const linkDialogInitialUrl = ref('');
    const linkDialogInitialText = ref('');
    const savedLinkRange = ref<{ index: number; length: number } | null>(null);

    // Show funnel option for email and display templates
    const showFunnelOption = computed(() => {
      const templateType = TemplateEditorState?.template?.type;
      return isDisplayTemplate(templateType) || isEmailTemplate(templateType);
    });

    // Custom link handler — replaces Quill's default tooltip
    const openLinkDialog = () => {
      const quill = editorRef.value?.quill;
      if (!quill) return;

      const range = quill.getSelection();
      if (!range) return;

      savedLinkRange.value = { index: range.index, length: range.length };

      // Detect existing link on selection
      const format = quill.getFormat(range);
      linkDialogInitialUrl.value = (typeof format?.link === 'string') ? format.link : '';
      linkDialogInitialText.value = range.length > 0 ? quill.getText(range.index, range.length) : '';

      isLinkDialogVisible.value = true;
    };

    const handleLinkConfirm = (result: { type: string; url: string; text: string }) => {
      const quill = editorRef.value?.quill;
      if (!quill || !savedLinkRange.value) return;

      const fallbackText = result.type === 'funnel' ? '🔗' : result.url;
      applyLinkToRange(quill, savedLinkRange.value, result.url, result.text ?? '', fallbackText);

      handleValueChange();

      // Sync funnel state if it's a redirect link
      // Use nextTick + setTimeout so the iframe DOM is updated before scanning
      if (result.url.includes('redirect_')) {
        nextTick(() => {
          setTimeout(() => refreshNextSteps(), 300);
        });
      }
    };

    const handleLinkRemove = () => {
      const quill = editorRef.value?.quill;
      if (!quill || !savedLinkRange.value) return;

      const hadRedirect = linkDialogInitialUrl.value.includes('redirect_');
      const { index, length } = savedLinkRange.value;

      quill.formatText(index, length, 'link', false, 'silent');

      handleValueChange();

      // Resync funnel if we removed a redirect link
      if (hadRedirect) {
        nextTick(() => {
          setTimeout(() => refreshNextSteps(), 300);
        });
      }
    };

    const checkInterval = ref();

    onBeforeMount(() => {
      editorKey.value += 1;

      // Avoids having an empty history point
      checkInterval.value = window.setInterval(() => {
        if (editorRef.value && editorRef.value.quill) {
          editorRef.value.quill.history.clear();
          window.clearInterval(checkInterval.value);
        }
      }, 200);
    });

    onMounted(async () => {
      const interval = setInterval(() => {
        const quillInstance = editorRef.value?.quill;
        if (quillInstance?.root) {
          // Override Quill's default link handler with our custom dialog
          const toolbar = quillInstance.getModule('toolbar');
          if (toolbar) {
            toolbar.addHandler('link', () => {
              openLinkDialog();
            });
          }

          // Suppress Quill's native link tooltip entirely — we rely on explicit
          // clicks (below) and the toolbar link icon to edit links
          const quillTooltip = quillInstance.theme?.tooltip;
          if (quillTooltip) {
            quillTooltip.show = () => { /* no-op */ };
          }

          // Click on an <a> inside the editor → open our dialog
          quillInstance.root.addEventListener('click', (event: MouseEvent) => {
            const target = event.target as HTMLElement | null;
            const anchor = target?.closest('a');
            if (!anchor || !quillInstance.root.contains(anchor)) return;

            event.preventDefault();
            event.stopPropagation();

            const blot = (Quill as any).find(anchor);
            if (!blot) return;
            const blotIndex = quillInstance.getIndex(blot);
            const blotLength = blot.length();

            savedLinkRange.value = { index: blotIndex, length: blotLength };
            linkDialogInitialUrl.value = anchor.getAttribute('data-spm-href') || anchor.getAttribute('href') || '';
            linkDialogInitialText.value = quillInstance.getText(blotIndex, blotLength);
            isLinkDialogVisible.value = true;
          });

          const { selector } = props.parserValues;
          const template = getTemplateIframeDocument();
          const refEl = template.querySelector(selector) as HTMLElement;
          registerEditor(editorRef.value?.quill?.root, refEl);
          const { applyBackgroundReferenceFromSelector } = useEditorBackground();
          applyBackgroundReferenceFromSelector(selector, template);

          // Add detected Google Fonts to Quill picker
          if (showGoogleFontsButton.value) {
            const detectedFonts = getDetectedTemplateFonts();
            detectedFonts.forEach((fontOption) => {
              addFontToQuillPicker(fontOption.label, fontOption.value);
              // Inject into parent document for Quill editor preview
              if (!isEmailSafe(fontOption.label)) {
                injectFontToIframe(fontOption.label, document);
              }
            });

            // Scan Quill content for inline Google Fonts not yet in whitelist
            const allElements = quillInstance.root.querySelectorAll('*');
            allElements.forEach((el: HTMLElement) => {
              const fontFamily = el.style?.fontFamily;
              if (fontFamily && FontStyle.whitelist && !FontStyle.whitelist.includes(fontFamily)) {
                const match = fontFamily.match(/"([^"]+)"/);
                if (match) {
                  const familyName = match[1];
                  if (!isEmailSafe(familyName)) {
                    addFontToQuillPicker(familyName, fontFamily);
                    if (template) {
                      injectFontToIframe(familyName, template);
                    }
                    injectFontToIframe(familyName, document);
                  }
                }
              }
            });
          }

          clearInterval(interval);
        }
      }, 100);

      let baseVariables = props.configs.options.variablesList || [];

      // Add custom contact properties to base variables (for use outside loops)
      try {
        const customSchemas = await getCustomContactProperties();
        const customVariables = customSchemas.flatMap((schema) => schema.properties.map((prop) => ({
          value: `{var=${prop.variableName}}`,
          label: `${schema.schemaName} - ${prop.name}`,
          labelWithBadge: `${schema.schemaName} - ${prop.name} <span class="custom-badge-inline">CUSTOM</span>`,
          type: prop.type,
          isCustom: true,
        })));
        baseVariables = [...baseVariables, ...customVariables];
      } catch (error) {
        console.error('Error loading custom contact properties:', error);
      }

      // Add metadata variables (custom events + game)
      try {
        const metadataVars = await getMetadataVariables();
        baseVariables = [...baseVariables, ...metadataVars];
      } catch (error) {
        console.error('Error loading metadata variables:', error);
      }

      // Enrich variables with collection context
      if (props.parserValues.selector) {
        enrichedVariables.value = await getVariablesWithCollectionContext(
          props.parserValues.selector,
          baseVariables,
        ) as never[];
      } else {
        enrichedVariables.value = baseVariables as never[];
      }
    });

    // Nettoyage avant démontage
    onBeforeUnmount(() => {
      flushValueChange();

      const el = editorRef.value?.quill?.root;
      if (el) unregisterEditor(el);

      if (editorRef.value) {
        if (editorRef.value.quill) {
          editorRef.value.quill = null;
        }
        editorRef.value = null;
      }

      if (overlayPanelRef.value) {
        overlayPanelRef.value = null;
      }
      if (overlayPanelBackgroundRef.value) {
        overlayPanelBackgroundRef.value = null;
      }
      if (overlayPanelLetterSpacingRef.value) {
        overlayPanelLetterSpacingRef.value = null;
      }

      if (checkInterval.value) {
        window.clearInterval(checkInterval.value);
      }
    });

    return {
      t,
      inputValue,
      enrichedVariables,
      editorRef,
      editorKey,
      overlayPanelRef,
      overlayPanelBackgroundRef,
      overlayPanelLetterSpacingRef,
      colorFormat,
      lineHeightList,
      letterSpacingValue,
      changeColor,
      toggleColorPicker,
      changeColorBackground,
      toggleColorPickerBackground,
      handleValueChange,
      handleSelectionChange,
      insertContent,
      replaceContent,
      saveRangeSelection,
      toggleLetterSpacing,
      changeLetterSpacing,
      // Google Fonts
      showGoogleFontsButton,
      isGoogleFontsModalVisible,
      isEmailTemplateType,
      iframeDocumentForModal,
      currentGoogleFontInQuill,
      openGoogleFontsModal,
      handleGoogleFontSelect,
      // Link Dialog
      isLinkDialogVisible,
      linkDialogInitialUrl,
      linkDialogInitialText,
      showFunnelOption,
      handleLinkConfirm,
      handleLinkRemove,
    };
  },
});
